import { compose } from 'react-recompose';

/**
 * Function that removes HTML tags from a string.
 * NOTE: It does not remove encoded HTML tags. It turns those into proper HTML tag strings. To properly remove HTML tags AND encoded HTML tags, you'll need to run this method in 2 successions.
 * @param {String} htmlText - a String with potential of HTML tags. If not given, falls back to empty string (otherwise it would return the 'undefined' as a string)
 * @return {String} - a first level parsed string that can contain HTML tags if they have been added as encoded, or stripped if they were direct HTML tags strings
 */
const stripHTML = (htmlText = '') => {
  const _div = document.createElement('div');
  _div.innerHTML = htmlText;
  const cleanText = _div.textContent || _div.innerText || '';
  _div.remove();

  return cleanText;
};

const _trimHTML = (html) => html.trim();

/**
 * A method that tries to turn an object into a string. This method can fail so it is wrapped in a try catch to make sure the failure is captured without breaking the process. We return a fall back on error along with the error itself, and on success we return no error and the stirngified object.
 *
 * @params {Object} objectToStringify - an object that we try to
 * @params {String} fallbackValue - a fallback value in case the strigify process fails
 * @return {Object}
 *         {String} error - the caught error in case if fails , OR null on success
 *         {String} text - the object turned to string on success, or the fallback value on fail
 */
const safelyStringify = (objectToStringify, fallbackValue = '') => {
  try {
    return {
      text:  JSON.stringify(objectToStringify, null, 4), //the last params are used to pretty up the strigified result with indentation
      error: null,
    };
  } catch (e) {
    return {
      text:  fallbackValue,
      error: e,
    };
  }
};

/**
 * The field value can contain ONLY HTML elements or white space or a combination of the 2 mentioned and this will  properly parse and trim those out.
 * NOTE: We need to run the strip HTML sequence twice as the first one removes the existing HTML tags and then turns the encoded HTML tags into proper HTML tags. The second run will remove the decoded HTML tags.
 * @param {String} htmlText - a String with potential of HTML tags or just a simple String with nothing special
 * @return {String} - a fully parsed string that contains no HTML tags and has trailing white spaces removed
 */
const stripAndTrimHTML = compose(_trimHTML, stripHTML, stripHTML);

const truncateWithEllipses = (text, max, ending = '…') =>
  text.length > max ? text.substr(0, max - ending.length) + ending : text;

/**
 * Splits a word into smaller chunks
 * @param {string} word to be split
 * @param {number} maxWordLength - length of the chunk of word
 * @returns
 */
const splitWord = (word, maxWordLength) => {
  const chunks = [];
  for (let i = 0; i < word.length; i += maxWordLength) {
    chunks.push(word.slice(i, i + maxWordLength));
  }
  return chunks;
};

/**
 * Splits a string into maxRowNum lines, to fit in the given maxLineLength
 * @param {string} string
 * @param {number} maxLineLength
 * @param {number} maxRowNum
 * @returns {array} - of strings
 */
const splitWordsToLines = (string, maxLineLength, maxRowNum) => {
  const words = string.split(' ');
  const lines = [];
  let currentLine = '';

  words.forEach((word) => {
    if (word.length > maxLineLength) {
      const chunks = splitWord(word, maxLineLength);
      if (currentLine) {
        lines.push(currentLine.trim());
        currentLine = '';
      }
      lines.push(...chunks);
    } else if ((currentLine + word).length <= maxLineLength) {
      currentLine += `${word} `;
    } else {
      lines.push(currentLine.trim());
      currentLine = `${word} `;
    }
  });
  if (currentLine) lines.push(currentLine.trim());
  if (lines.length > maxRowNum) {
    const truncatedLines = lines.slice(0, maxRowNum);
    truncatedLines[truncatedLines.length - 1] = truncatedLines[truncatedLines.length - 1] + '…';
    return truncatedLines;
  } else {
    return lines;
  }
};

export default {
  stripHTML,
  stripAndTrimHTML,
  safelyStringify,
  truncateWithEllipses,
  splitWordsToLines,
};
